import { CommonModule } from '@angular/common';
import { Component, inject, viewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTabsModule } from '@angular/material/tabs';
import { ApiService } from '../../../services/api.service';
import { SpinnerService } from '../../../shared/spinner/spinner.service';
import { AlertService } from '../../../shared/alert/alert.service';
import { AlertEnum } from '../../../shared/alert/alert.enum';
import { TreeItem, TreeViewModule } from '@progress/kendo-angular-treeview';
import { ListViewModule } from "@progress/kendo-angular-listview";
import { IDocument } from '../../../interfaces/i-document';
import { SVGIcon, minusOutlineIcon, xOutlineIcon } from "@progress/kendo-svg-icons";
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { GridModule } from '@progress/kendo-angular-grid';
import {
  FileRestrictions,
  FileSelectComponent,
  FileState,
  SelectEvent,
  UploadsModule
} from "@progress/kendo-angular-upload";
import { ChatSessionService } from "../services/chat-session.service";
import { ISensitivityLabel } from "../../../interfaces/i-sensitivity-label";
import { ProfileService } from "../../../services/profile.service";
import { ChatDocumentService } from "../services/chat-document.service";
import { SENSITIVITY_LABELS_FLAT } from '../../../constants/sensitivity.constants';
import { AgentService } from '../../agent/services/agent.service';
import { AttachmentDocComponent } from "../../../shared/attachment-doc/attachment-doc.component";
import { ConversationStarterService } from '../services/conversation-starter.service';

interface IErrorFile {
  name: string;
  sensitivityLabel?: {
    id: number;
    label: string;
  };
  reason: string;
}

@Component({
  selector: 'app-document-manager',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatTabsModule,
    TreeViewModule,
    ListViewModule,
    ButtonsModule,
    UploadsModule,
    GridModule,
    AttachmentDocComponent,
  ],
  templateUrl: './document-manager.component.html',
  styleUrl: './document-manager.component.scss'
})
export class DocumentManagerComponent {
  fileSelectComponent = viewChild.required(FileSelectComponent);
  maxDocuments: number;
  activeTabIndex = 0; // 0 = OneDrive tab, 1 = Upload tab
  sessionId: string | number;

  // kendo-angular-treeview
  expandedKeys: any[] = [ "0", "1" ];
  selectedKeys: any[] = [ "0_2" ];
  data: any[] = [];
  filterable = false;
  // END kendo-angular-treeview

  // kendo-angular-upload
  fileRestrictions: FileRestrictions;
  uploadFileForm: FormGroup;
  searchForm: FormGroup;
  myFiles: Array<any> = [];
  // END kendo-angular-upload

  // Error files table
  errorFiles: IErrorFile[] = [];
  displayedColumns: string[] = ['name', 'sensitivityLabel', 'reason'];

  readonly minusOutlineIcon: SVGIcon = minusOutlineIcon;
  readonly xOutlineIcon: SVGIcon = xOutlineIcon;

  chatDocumentService = inject(ChatDocumentService);
  #apiService = inject(ApiService);
  #spinnerService = inject(SpinnerService);
  #alertService = inject(AlertService);
  #formBuilder = inject(FormBuilder);
  #chatSessionService = inject(ChatSessionService);
  #profileService = inject(ProfileService);
  #agentService = inject(AgentService);
  #conversationStarterService = inject(ConversationStarterService);
  #dialogData = inject(MAT_DIALOG_DATA, { optional: true });

  constructor() {
    this.sessionId = this.#dialogData?.sessionId ?? this.#chatSessionService.selectedSessionId();

    this.fileRestrictions = {
      maxFileSize: this.#profileService.userProfile()?.config?.maxDocumentSize!,
    };

    this.maxDocuments = this.#profileService.userProfile()?.config?.maxDocuments!;

    this.uploadFileForm = this.#formBuilder.group({
      files: [ this.myFiles, [ Validators.required ] ],
    });

    this.searchForm = this.#formBuilder.group({
      search: [ '' ],
    });
  }

  /**
   * Format the sensitivity label for display
   */
  getSensitivityLabelDisplay(sensitivityLabel: { id: number, label: string } | undefined): string {
    if (!sensitivityLabel) {
      return 'N/A';
    }
    return this.#agentService.getSensitivityLabelString(sensitivityLabel);
  }

  /**
   * Search for documents in the OneDrive.
   */
  search(fileName?: string): void {
    this.#spinnerService.show();
    this.filterable = false;

    // If searching from file upload tab, switch to OneDrive tab to show results
    if (this.activeTabIndex === 0 && !fileName) {
      this.activeTabIndex = 1;
    }

    const requestBody = {
      criteria: fileName ?? this.searchForm.get('search')?.value,
      type: 'click'
    };

    this.#apiService.documentSearch(requestBody).subscribe({
      next: response => {
        if (response.length === 0) {
          this.#alertService.showAlert({
            message: `"${requestBody.criteria}" was not found in your OneDrive.`,
            duration: 10,
            type: AlertEnum.info,
          });
        } else {
          const session = this.#chatSessionService.chatSessions()?.find(s => s.id === this.sessionId);
          let sl = session?.sensitivityLabels?.[0];

          if (this.#conversationStarterService.isStartConversation()) {
            // TODO add logic for Start Conversation page
            sl = this.#conversationStarterService.conversation()!.sensitivityLabel;
          }

          if (!sl) {
            this.#alertService.showAlert({
              message: 'Unable to load session sensitivity label. Please try again.',
              type: AlertEnum.error,
            });
            this.#spinnerService.hide();
            return;
          }

          // We don't know what the shape of response is, but it will have the property called 'result' which is the doc
          // meta data.  We have to extract that first.
          const docs = this.#filterDocuments(response, sl);
          // Now organize the data into a folder structure based on the 'path' property.
          this.data = this.#convertToNestedStructure(docs);

          this.filterable = true;
        }
      },
      error: (error: any) => {
        this.#alertService.showAlert({
          message: `Failed to get the document list. ${error.message}`,
          type: AlertEnum.error,
        });
        this.#spinnerService.hide();
      },
      complete: () => {
        this.#spinnerService.hide();
      }
    });
  }

  /**
   * This method is called when the user selects a document from the treeview.
   */
  onTreeSelectionChange(event: TreeItem): void {
    const selectedItem = event.dataItem;

    if (this.chatDocumentService.selectedDocuments().length >= this.maxDocuments) {
      this.#alertService.showAlert({
        message: `The max documents that you can attach to a prompt is ${this.maxDocuments}.`,
        type: AlertEnum.warn,
      });
    } else {
      // Make sure the selected item is a document
      if (Object.hasOwn(selectedItem, 'name')) {
        // Check if the selected item is in the selectedDocuments list
        if (this.#isItemSelectedDocument(selectedItem.text)) {
          // Remove the document from the selectedDocuments list
          this.removeDocFromList(selectedItem);
        } else {
          // Add the document to the selectedDocuments list
          this.chatDocumentService.selectedDocuments.update(documents => [ ...documents, selectedItem ]);

          // Update the chat session input data with the new documents
          this.#updateChatSessionInputDataWithDocuments();
        }
      }
    }
  }

  /**
   * Returns the icon class for the treeview item.
   */
  iconClass({ text, items }: any): any {
    if (this.#hasFileExtension(text) && this.#isItemSelectedDocument(text)) {
      return 'icon-tree-item-minus';
    }
    return 'icon-tree-item-plus';
  }

  isFileItem({ text, items }: any): boolean {
    return this.#hasFileExtension(text) && !items?.length;
  }

  isFileListed({ text, items }: any): string {
    if (this.#hasFileExtension(text) && this.#isItemSelectedDocument(text)) {
        return 'do_not_disturb_on';
    }
    return 'add_circle';
  }

  /**
   * Checks if the treeview item has children.
   */
  hasChildren = (item: any) => item.items && item.items.length > 0;

  /**
   * Removes the document from the selectedDocuments list.
   */
  removeDocFromList(doc: IDocument): void {
    const updatedDocumentList = this.chatDocumentService.selectedDocuments().filter((d) => d.name !== doc.name);
    this.chatDocumentService.selectedDocuments.set([ ...updatedDocumentList ]);

    if (updatedDocumentList.length === 0) {
      this.clearFileSelect();
    }
  }

  /**
   * Sets the active tab index
   */
  setActiveTab(index: number): void {
    this.activeTabIndex = index;
  }

  /**
   * Uploads the file to the OneDrive.
   * This is automatically called after file selection.
   */
  uploadFile(): void {
    const totalSelectedDocuments = this.chatDocumentService.selectedDocuments().length;
    if (totalSelectedDocuments === this.maxDocuments) {
      this.#alertService.showAlert({
        message: `The max documents that you can attach is ${this.maxDocuments}.`,
        type: AlertEnum.warn,
      });
      return;
    }

    if ((this.myFiles.length + totalSelectedDocuments) > this.maxDocuments) {
      this.#alertService.showAlert({
        message: `The max documents that you can attach is ${this.maxDocuments}. You will need to remove documents from your selected documents or from your uploaded documents.`,
        type: AlertEnum.warn,
        duration: 10
      });
      return;
    }

    this.#spinnerService.show();

    if (this.uploadFileForm.invalid) {
      this.#spinnerService.hide();
      return;
    } else {
      const filesToUpload = [ ...this.myFiles ]; // Create a copy of the myFiles array
      const filesToRemove: any[] = []; // Array to keep track of files to remove

      // Iterate over the files to upload
      filesToUpload.forEach((fileObj, index) => {
        const formData = new FormData();
        const file = fileObj.rawFile; // Access the raw file from the Kendo file object
        formData.append("file", file, file.name);

        // Call the file upload service
        this.#apiService.fileUpload(formData).subscribe({
          next: response => {
            if (response.sensitivityLabel === null) {
              this.#addDocumentToSelectedList(response, file, fileObj, index, filesToUpload, filesToRemove);
            } else {
              const session = this.#chatSessionService.chatSessions()?.find(s => s.id === this.sessionId);
              let currentSensitivityLabel = session?.sensitivityLabels?.[0]!;
              if (this.#conversationStarterService.isStartConversation()) {
                // TODO add logic for Start Conversation page
                // TODO commented this out for now as per Jeff saying we will add this later
                // if we don't have any agents, we set the SL by the attached docs.
                // if we have agents, but they are all "alwaysAvailable", we can set the SL by the attached docs.
                // else, we use the agents SL to avoid issues with setting the SL lower than what the agents have
                // const agents = this.#conversationStarterService.conversation()?.agents;
                // const areAllAgentsAlwaysAvailable = agents?.every(agentId => {
                //   const agent = this.#agentService.agents().agents.find(a => a.id === agentId);
                //   return agent?.details?.alwaysAvailable;
                // });

                // if (!agents?.length || areAllAgentsAlwaysAvailable) {
                //   this.#conversationStarterService.setSensitivityLabelByDocuments([response]);
                // }

                currentSensitivityLabel = this.#conversationStarterService.conversation()!.sensitivityLabel;
              }

              if (this.#conversationStarterService.setSensitivityLabelByAttachedDocuments()) {
                this.#conversationStarterService.setSensitivityLabelByDocuments([response]);
                const updatedSession = this.#chatSessionService.chatSessions()?.find(s => s.id === this.sessionId);
                currentSensitivityLabel = updatedSession?.sensitivityLabels?.[0]!;
              }

              const docSensitivityLabel = SENSITIVITY_LABELS_FLAT.find(label => label.guid === response.sensitivityLabel?.guid);

              if (docSensitivityLabel) {
                // Check if the document Sensitivity Label has a parent ID
                const parentId = docSensitivityLabel.parentId;
                // Compare the current Sensitivity Label ID with the parent ID if it exists
                const comparisonId = parentId ? parentId : docSensitivityLabel.id;

                // Check if the current Sensitivity Label ID is greater than or equal to the comparison ID
                if (currentSensitivityLabel.id >= comparisonId) {
                  this.#addDocumentToSelectedList(response, file, fileObj, index, filesToUpload, filesToRemove);
                } else {
                  const responseSensitivityLabel = this.#agentService.getSensitivityLabelString({ id: response.sensitivityLabel.id, label: response.sensitivityLabel.label});
                  const errorMessage = `The file must have a Sensitivity Label equal to or lower than the Session's Sensitivity Label.`;

                  this.#alertService.showAlert({
                    message: `The file "${response.name}" with Sensitivity Label "${responseSensitivityLabel}" cannot be attached. ${errorMessage}`,
                    type: AlertEnum.warn,
                  });

                  // Add to error files with a new array to ensure change detection
                  this.errorFiles = [
                    ...this.errorFiles,
                    {
                      name: response.name,
                      sensitivityLabel: response.sensitivityLabel,
                      reason: errorMessage
                    }
                  ];

                  this.uploadFileForm.reset();
                }
              } else {
                // this route is for when the Sensitivity Label is not in the flat list/owned by CGI
                this.#addDocumentToSelectedList(response, file, fileObj, index, filesToUpload, filesToRemove);
              }
            }
          },
          error: (error: any) => {
            let errorMessage = `Failed to upload the document. ${error.statusText}`;
            let reason = error.statusText || 'Upload error';

            if (error.status === 413) {
              errorMessage = `Failed to upload the document. File exceeds maximum allowed document length.`;
              reason = 'File exceeds maximum allowed document length';
            }

            if (error.status === 415) {
              errorMessage = `File was uploaded successfully but is not supported for CGI Assist.`;
              reason = 'File format not supported';
            }

            this.#spinnerService.hide();

            this.#alertService.showAlert({
              message: errorMessage,
              type: AlertEnum.error,
            });

            // Add to error files - for uploaded files we might not have all details about the file
            if (fileObj && fileObj.rawFile) {
              // Create a new array to ensure change detection picks up the new item
              this.errorFiles = [
                ...this.errorFiles,
                {
                  name: fileObj.rawFile.name,
                  reason: reason
                }
              ];
            }
          },
          complete: () => {
            this.#spinnerService.hide();
          }
        });

        // Remove the processed files from the FileSelectComponent
        filesToRemove.forEach(fileObj => {
          this.removeFileFromFileSelect(this.fileSelectComponent(), fileObj.uid);
        });

        // Clear the myFiles array
        this.myFiles = [];
      });
    }
  }

  /**
   * The method is called when the user selects a file in the Kendo UI file select (upload) component.
   * Automatically uploads the file after selection.
   */
  onSelectFileEvent(e: SelectEvent): void {
    let validFilesSelected = false;

    e.files.forEach((file) => {
      if (!file.validationErrors) {
        this.myFiles.push(file);
        // Search for the file in the OneDrive
        this.search(file.name);
        validFilesSelected = true;
      }
    });

    // Update the form control value with the new files array
    if (validFilesSelected) {
      // Update the form control with the current files
      this.uploadFileForm.get('files')?.setValue(this.myFiles);
      this.uploadFileForm.get('files')?.updateValueAndValidity();

      // After ensuring the form is updated, trigger the upload
      this.uploadFile();
    }
  }

  /**
   * Removes the file from the Kendo UI file select (upload) component.
   */
  removeFileFromFileSelect(fileSelect: FileSelectComponent, uid: string): void {
    const fileToRemove = this.myFiles.find(file => file.uid === uid);
    if (fileToRemove) {
      fileSelect.removeFileByUid(uid);
      this.myFiles = this.myFiles.filter(file => file.uid !== uid);
    }
    if (this.myFiles.length === 0) {
      this.uploadFileForm.reset();
      this.clearFileSelect();
    }
  }

  /**
   * Clears the file select (upload) component.
   */
  clearFileSelect() {
    this.myFiles = [];
    this.chatDocumentService.selectedDocuments.set([]);
    if (!this.#conversationStarterService.isStartConversation()) {
      const currentInputData = this.#chatSessionService.getSessionInputData(this.sessionId);
      this.#chatSessionService.setSessionInputData(this.sessionId, {
        ...currentInputData,
        documents: []
      });
    }
  }

  /**
   * Displays the button based on the file state in the Kendo UI file select (upload) component.
   * This is the button to remove the file from the file upload component.
   */
  showButton(state: FileState): boolean {
    return state === FileState.Selected ? true : false;
  }

  /**
   * Checks if the document is selected.
   */
  #isItemSelectedDocument(name: string): boolean {
    return this.chatDocumentService.selectedDocuments().some((d) => d.name === name);
  }

  /**
   * This method is called when the user clicks on the treeview item.
   */
  #hasFileExtension(text: string): boolean {
    const extensionPattern = /\.[0-9a-z]+$/i; // Matches any file extension
    return extensionPattern.test(text);
  }

  /**
   * Filters the property called 'result' from each record in the JSON and creates an IDocument[] array.
   */
  #filterDocuments(json: any[], sensitivityLabel: ISensitivityLabel): IDocument[] {
    return json
      .map(record => record.result)
      .filter((result: IDocument) => result.sensitivityLabel === null || result.sensitivityLabel.id <= sensitivityLabel.id
      );
  }

  /**
   * Converts a flat array of data into a nested tree structure.
   * Each item in the data array should have a path property that indicates its position in the tree.
   * The method splits the path into parts and constructs the tree accordingly.
   */
  #convertToNestedStructure(data: any[]): any[] {
    const tree: any[] = [];

    data.forEach(item => {
      const parts = item.path ? item.path.split('/').filter(Boolean) : [];
      let currentLevel = tree;

      parts.forEach((part: any, index: any) => {
        let existingPath = currentLevel.find((node: any) => node.text === part);

        if (!existingPath) {
          existingPath = { text: part, items: [] };
          currentLevel.push(existingPath);
        }

        if (index === parts.length - 1) {
          existingPath.items.push({ ...item, text: item.name });
        } else {
          currentLevel = existingPath.items;
        }
      });

      if (parts.length === 0) {
        tree.push({ ...item, text: item.name, items: [] });
      }
    });

    return this.#sortTree(tree);
  }

  /**
   * Sorts the tree nodes alphabetically by their text property.
   * Recursively sorts the child nodes if they exist.
   */
  #sortTree(nodes: any[]): any[] {
    return nodes.sort((a, b) => a.text.localeCompare(b.text)).map(node => {
      if (node.items) {
        node.items = this.#sortTree(node.items);
      }
      return node;
    });
  }

  #addDocumentToSelectedList(response: any, file: any, fileObj: any, index: number, filesToUpload: any[], filesToRemove: any[]): void {
    // Add the document to the selectedDocuments list
    this.chatDocumentService.selectedDocuments.update(documents => [ ...documents, response ]);

    // Update the chat session input data with the new documents
    this.#updateChatSessionInputDataWithDocuments();

    if (index + 1 === filesToUpload.length) {
      // Reset the form only if we're on the last record of all the selected files
      this.uploadFileForm.reset();
    }

    this.#alertService.showAlert({
      message: `File, "${file.name}", uploaded successfully and added to the list.`,
      type: AlertEnum.success,
    });

    // Add the file to the filesToRemove array
    filesToRemove.push(fileObj);
  }

  /**
   * Updates the chat session input data with the currently selected documents.
   * This ensures that the current chat session state is always in sync with the selected documents.
   *
   */
  #updateChatSessionInputDataWithDocuments() {
    const documents = this.chatDocumentService.selectedDocuments();

    if (!this.#conversationStarterService.isStartConversation()) {
      this.#chatSessionService.updateSessionInputDataDocuments(documents);
    }
  }
}
